import React from 'react';
import { 
  CreditCardIcon, 
  UserIcon, 
  CalendarIcon,
  DocumentTextIcon,
  BanknotesIcon,
  ClockIcon,
  BuildingLibraryIcon,
  TicketIcon
} from '@heroicons/react/24/outline';

const PaymentDetail = ({ payment }) => {
  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Render status badge
  const renderStatusBadge = (status) => {
    // Default styling for unknown or undefined status
    let bgColor = 'bg-gray-100';
    let textColor = 'text-gray-800';
    
    // Only process status if it exists
    if (status) {
      switch (status.toLowerCase()) {
        case 'completed':
          bgColor = 'bg-green-100';
          textColor = 'text-green-800';
          break;
        case 'pending':
          bgColor = 'bg-yellow-100';
          textColor = 'text-yellow-800';
          break;
        case 'failed':
          bgColor = 'bg-red-100';
          textColor = 'text-red-800';
          break;
        case 'refunded':
          bgColor = 'bg-purple-100';
          textColor = 'text-purple-800';
          break;
        default:
          break;
      }
    }
    
    return (
      <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${bgColor} ${textColor}`}>
        {status || 'Unknown'}
      </span>
    );
  };

  // Get payment method icon
  const getPaymentMethodIcon = (method) => {
    // Default to credit card icon if method is undefined or null
    if (!method) {
      return <CreditCardIcon className="h-6 w-6" aria-hidden="true" />;
    }
    
    switch (method.toLowerCase()) {
      case 'credit card':
        return <CreditCardIcon className="h-6 w-6" aria-hidden="true" />;
      case 'paypal':
        return <BuildingLibraryIcon className="h-6 w-6" aria-hidden="true" />;
      case 'bank transfer':
        return <BuildingLibraryIcon className="h-6 w-6" aria-hidden="true" />;
      case 'm-pesa':
        return <BanknotesIcon className="h-6 w-6" aria-hidden="true" />;
      default:
        return <CreditCardIcon className="h-6 w-6" aria-hidden="true" />;
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-start mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Payment Details</h2>
        {renderStatusBadge(payment?.status)}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Payment Information */}
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Transaction Information</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                    <DocumentTextIcon className="h-6 w-6" aria-hidden="true" />
                  </span>
                </div>
                <div className="ml-4">
                  <dt className="text-sm font-medium text-gray-500">Transaction ID</dt>
                  <dd className="mt-1 text-sm text-gray-900">{payment?.transaction_id || 'N/A'}</dd>
                </div>
              </div>
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                    {getPaymentMethodIcon(payment?.payment_method)}
                  </span>
                </div>
                <div className="ml-4">
                  <dt className="text-sm font-medium text-gray-500">Payment Method</dt>
                  <dd className="mt-1 text-sm text-gray-900">{payment?.payment_method || 'N/A'}</dd>
                </div>
              </div>
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                    <BanknotesIcon className="h-6 w-6" aria-hidden="true" />
                  </span>
                </div>
                <div className="ml-4">
                  <dt className="text-sm font-medium text-gray-500">Amount</dt>
                  <dd className="mt-1 text-sm font-semibold text-gray-900">{payment?.amount ? formatCurrency(payment.amount) : 'N/A'}</dd>
                </div>
              </div>
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                    <CalendarIcon className="h-6 w-6" aria-hidden="true" />
                  </span>
                </div>
                <div className="ml-4">
                  <dt className="text-sm font-medium text-gray-500">Date & Time</dt>
                  <dd className="mt-1 text-sm text-gray-900">{payment?.date ? formatDate(payment.date) : 'N/A'}</dd>
                </div>
              </div>
              {payment?.processor_response && (
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                      <ClockIcon className="h-6 w-6" aria-hidden="true" />
                    </span>
                  </div>
                  <div className="ml-4">
                    <dt className="text-sm font-medium text-gray-500">Processor Response</dt>
                    <dd className="mt-1 text-sm text-gray-900">{payment?.processor_response || 'N/A'}</dd>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Customer Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Customer Information</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                    <UserIcon className="h-6 w-6" aria-hidden="true" />
                  </span>
                </div>
                <div className="ml-4">
                  <dt className="text-sm font-medium text-gray-500">Customer Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{payment.customer?.name || 'N/A'}</dd>
                </div>
              </div>
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                    <DocumentTextIcon className="h-6 w-6" aria-hidden="true" />
                  </span>
                </div>
                <div className="ml-4">
                  <dt className="text-sm font-medium text-gray-500">Email</dt>
                  <dd className="mt-1 text-sm text-gray-900">{payment.customer?.email || 'N/A'}</dd>
                </div>
              </div>
              {payment.customer?.phone && (
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                      <DocumentTextIcon className="h-6 w-6" aria-hidden="true" />
                    </span>
                  </div>
                  <div className="ml-4">
                    <dt className="text-sm font-medium text-gray-500">Phone</dt>
                    <dd className="mt-1 text-sm text-gray-900">{payment.customer?.phone || 'N/A'}</dd>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Payment Notes */}
          {payment?.notes && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Notes</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-900">{payment?.notes || 'No notes available'}</p>
              </div>
            </div>
          )}
        </div>

        {/* Booking Information */}
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Booking Information</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                    <TicketIcon className="h-6 w-6" aria-hidden="true" />
                  </span>
                </div>
                <div className="ml-4">
                  <dt className="text-sm font-medium text-gray-500">Booking Number</dt>
                  <dd className="mt-1 text-sm text-gray-900">{payment?.booking?.booking_number || 'N/A'}</dd>
                </div>
              </div>
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                    <DocumentTextIcon className="h-6 w-6" aria-hidden="true" />
                  </span>
                </div>
                <div className="ml-4">
                  <dt className="text-sm font-medium text-gray-500">Tour</dt>
                  <dd className="mt-1 text-sm text-gray-900">{payment?.booking?.tour?.name || 'N/A'}</dd>
                </div>
              </div>
              {payment?.booking?.date && (
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                      <CalendarIcon className="h-6 w-6" aria-hidden="true" />
                    </span>
                  </div>
                  <div className="ml-4">
                    <dt className="text-sm font-medium text-gray-500">Tour Date</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {payment?.booking?.date ? formatDate(payment.booking.date) : 'N/A'}
                    </dd>
                  </div>
                </div>
              )}
              {payment?.booking?.travelers && (
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                      <UserIcon className="h-6 w-6" aria-hidden="true" />
                    </span>
                  </div>
                  <div className="ml-4">
                    <dt className="text-sm font-medium text-gray-500">Number of Travelers</dt>
                    <dd className="mt-1 text-sm text-gray-900">{payment?.booking?.travelers || 'N/A'}</dd>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Payment Timeline */}
          {payment?.timeline && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Payment Timeline</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flow-root">
                  <ul className="-mb-8">
                    {payment?.timeline?.map((event, eventIdx) => (
                      <li key={eventIdx}>
                        <div className="relative pb-8">
                          {eventIdx !== payment?.timeline?.length - 1 ? (
                            <span
                              className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                              aria-hidden="true"
                            />
                          ) : null}
                          <div className="relative flex space-x-3">
                            <div>
                              <span className="h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white bg-primary-light">
                                <ClockIcon className="h-5 w-5 text-white" aria-hidden="true" />
                              </span>
                            </div>
                            <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                              <div>
                                <p className="text-sm text-gray-500">
                                  {event?.description || 'Event'}{' '}
                                  {event?.details && <span className="font-medium text-gray-900">{event?.details}</span>}
                                </p>
                              </div>
                              <div className="text-right text-sm whitespace-nowrap text-gray-500">
                                {event?.date ? formatDate(event.date) : 'N/A'}
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Receipt/Invoice */}
          <div className="mt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Receipt</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="border-t border-gray-200 pt-4">
                <dl className="divide-y divide-gray-200">
                  <div className="py-2 flex justify-between text-sm">
                    <dt className="text-gray-500">Subtotal</dt>
                    <dd className="text-gray-900 font-medium">{payment?.amount ? formatCurrency(payment.amount) : 'N/A'}</dd>
                  </div>
                  {payment?.tax_amount && (
                    <div className="py-2 flex justify-between text-sm">
                      <dt className="text-gray-500">Tax</dt>
                      <dd className="text-gray-900 font-medium">{payment?.tax_amount ? formatCurrency(payment.tax_amount) : 'N/A'}</dd>
                    </div>
                  )}
                  {payment?.discount_amount && (
                    <div className="py-2 flex justify-between text-sm">
                      <dt className="text-gray-500">Discount</dt>
                      <dd className="text-gray-900 font-medium">-{payment?.discount_amount ? formatCurrency(payment.discount_amount) : 'N/A'}</dd>
                    </div>
                  )}
                  <div className="py-2 flex justify-between text-sm font-medium">
                    <dt className="text-gray-900">Total</dt>
                    <dd className="text-primary">{payment?.amount ? formatCurrency(payment.amount) : 'N/A'}</dd>
                  </div>
                </dl>
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  Download Receipt
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentDetail;
